import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

interface Product {
  id: number | string;
  image: string;
  title: string;
  description: string;
  price: string;
}

interface CartContextType {
  cartItems: Product[];
  addToCart: (item: Product) => void;
  removeFromCart: (productId: number | string) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cartItems, setCartItems] = useState<Product[]>([]);

  const addToCart = useCallback((item: Product) => {
    setCartItems((prevItems) => {
      if (!prevItems.find((p) => p.id === item.id)) {
        return [...prevItems, item];
      }
      return prevItems;
    });
  }, []);

  const removeFromCart = useCallback((productId: number | string) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== productId));
  }, []);

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
    }),
    [cartItems, addToCart, removeFromCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
